import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  FileText,
  Check,
  Clock,
  AlertCircle,
  Eye,
  Trash2,
  X,
  Plus,
  ChevronDown,
  Calendar,
  Building,
  Download,
  Upload,
  Edit
} from 'lucide-react';
import { formatCurrency, getCurrencyFontClass } from '../utils/currency';
import { currenciesAPI, departmentsAPI, vendorInvoicesAPI, vendorsAPI, financialPowersAPI } from '../services/api';
import { Currency, Department, Vendor } from '../types';
import toast from 'react-hot-toast';
import { errorModal } from '../components/ErrorModal';
import { confirmToast } from '../components/confirmToast';
import Spinner from '../components/Spinner';
import { decryptData } from '../utils/encryption';
import { useAuth } from '../contexts/AuthContext';
import Pagination from '../components/Pagination';

interface FinancialProposal {
  id: number;
  proposalCode?: string;
  vendor_name?: string;
  department_name?: string;
  invoice_type: string;
  amount: number;
  currency: string;
  created_at: string;
  status: string;
  vendor_id?: number;
  department_id?: number;
  approver_details?: any[];
  proposal_document_url?: string;
  proposal_document_filename?: string;
  proposal_document_size?: number;
  additional_attachments?: any[];
  approvers?: number[];
  vendor_email?: string;
  vendor_phone?: string;
  vendor_address?: string;
  reference_number?: string;
  reference_name?: string;
}

const invoiceTypeOptions = [
  { id: 'variable', label: 'Variable Payment' },
  { id: 'fixed', label: 'Fixed Payment' },
];

const FinancialPowerProposal: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userPermissionsLocalStorage = localStorage.getItem('permissions') || '';
  const userPermissions = decryptData(userPermissionsLocalStorage);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | string>('all');
  const [departments, setDepartments] = useState<Department[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [financialPowers, setFinancialPowers] = useState<FinancialProposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    total_pages: 0,
  });

  const [showProposalModal, setShowProposalModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProposal, setEditingProposal] = useState<FinancialProposal | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState<FinancialProposal | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [auditTrailData, setAuditTrailData] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);
  // Track which specific approval action is being submitted: "approverId-action"
  const [submittingApproval, setSubmittingApproval] = useState<string | null>(null);
  const [approverNotes, setApproverNotes] = useState<{ [key: number]: string }>({});
  const [referenceName, setReferenceName] = useState<string>('');
  const [showAttachmentViewer, setShowAttachmentViewer] = useState(false);
  const [currentAttachmentUrl, setCurrentAttachmentUrl] = useState<string>('');
  const [currentAttachmentName, setCurrentAttachmentName] = useState<string>('');
  const [loadingAttachment, setLoadingAttachment] = useState(false);
  const [originalAttachmentUrl, setOriginalAttachmentUrl] = useState<string>('');
  const [departmentDropdownOpen, setDepartmentDropdownOpen] = useState(false);
  const [vendorDropdownOpen, setVendorDropdownOpen] = useState(false);
  const [invoiceTypeDropdownOpen, setInvoiceTypeDropdownOpen] = useState(false);
  const [currencyDropdownOpen, setCurrencyDropdownOpen] = useState(false);
  const [approverDropdownOpenIndex, setApproverDropdownOpenIndex] = useState<number | null>(null);
  const [departmentSearchTerm, setDepartmentSearchTerm] = useState('');
  const [vendorSearchTerm, setVendorSearchTerm] = useState('');
  const [invoiceTypeSearchTerm, setInvoiceTypeSearchTerm] = useState('');
  const [currencySearchTerm, setCurrencySearchTerm] = useState('');
  const [formErrors, setFormErrors] = useState<{
    departmentId?: string;
    vendorId?: string;
    invoiceType?: string;
    currency?: string;
    amount?: string;
    proposalDocument?: string;
    approvers?: string;
  }>({});
  const departmentDropdownRef = useRef<HTMLDivElement>(null);
  const vendorDropdownRef = useRef<HTMLDivElement>(null);
  const invoiceTypeDropdownRef = useRef<HTMLDivElement>(null);
  const currencyDropdownRef = useRef<HTMLDivElement>(null);
  const approverDropdownRefs = useRef<(HTMLDivElement | null)[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  const [proposalForm, setProposalForm] = useState({
    departmentId: '',
    vendorId: '',
    invoiceType: '',
    currency: '',
    amount: '',
    proposalDocument: null as File | null,
    additionalAttachments: [] as File[],
    approvers: ['']
  });

  const stats = useMemo(() => {
    return {
      total: pagination.total,
      approved: financialPowers.filter((proposal) => proposal.status === 'approved').length,
      pending: financialPowers.filter((proposal) => proposal.status === 'pending' || proposal.status?.startsWith('pending_')).length,
      underReview: financialPowers.filter((proposal) => proposal.status?.startsWith('pending_approver_')).length
    };
  }, [financialPowers, pagination.total]);

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [deptRes, vendorRes, currencyRes] = await Promise.all([
          departmentsAPI.dropDown(),
          vendorsAPI.allVendorsDropdown(),
          vendorInvoicesAPI.invoiceApproverDropdown(),
          currenciesAPI.dropDown()
        ]);

        if (deptRes?.success) {
          setDepartments(deptRes.data || []);
        }

        if (vendorRes?.success) {
          setVendors(vendorRes.data || []);
        }


        if (currencyRes?.success) {
          setCurrencies(currencyRes.data || []);
        }
      } catch (error) {
        console.error('Failed to load proposal dropdown data', error);
      }
    };

    fetchDropdownData();
    // fetchFinancialPowers();
  }, []);

  const fetchFinancialPowers = async () => {
    setLoading(true);
    try {
      const params: any = {
        page: pagination.page,
        limit: pagination.limit,
      };
      if (searchTerm) params.search = searchTerm;
      if (statusFilter !== 'all') params.status = statusFilter;

      const response = await financialPowersAPI.getAll(params);
      if (response?.success) {
        setFinancialPowers(response.data?.financial_powers || []);
        if (response.data?.pagination) {
          setPagination({
            page: response.data.pagination.page || 1,
            limit: response.data.pagination.limit || 20,
            total: response.data.pagination.total || 0,
            totalPages: response.data.pagination.total_pages || response.data.pagination.totalPages || 0,
            total_pages: response.data.pagination.total_pages || response.data.pagination.totalPages || 0,
          });
        }
      }
    } catch (error) {
      console.error('Failed to fetch financial powers', error);
      errorModal('Failed to load financial powers');
    } finally {
      setLoading(false);
    }
  };

  // useEffect(() => {
  //   fetchFinancialPowers();
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [ statusFilter]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (pagination.page === 1) {
        fetchFinancialPowers();
      } else {
        setPagination(prev => ({ ...prev, page: 1 }));
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, statusFilter]);

  // Fetch data when pagination changes
  useEffect(() => {
    fetchFinancialPowers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.limit]);

  const filteredVendors = useMemo(() => vendors, [vendors]);
  const filteredCurrencies = useMemo(() => {
    if (!currencySearchTerm) return currencies;
    return currencies.filter((currency) =>
      currency.name.toLowerCase().includes(currencySearchTerm.toLowerCase())
    );
  }, [currencies, currencySearchTerm]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (departmentDropdownRef.current && !departmentDropdownRef.current.contains(event.target as Node)) {
        setDepartmentDropdownOpen(false);
      }
      if (vendorDropdownRef.current && !vendorDropdownRef.current.contains(event.target as Node)) {
        setVendorDropdownOpen(false);
      }
      if (invoiceTypeDropdownRef.current && !invoiceTypeDropdownRef.current.contains(event.target as Node)) {
        setInvoiceTypeDropdownOpen(false);
      }
      if (currencyDropdownRef.current && !currencyDropdownRef.current.contains(event.target as Node)) {
        setCurrencyDropdownOpen(false);
      }
      if (
        approverDropdownOpenIndex !== null &&
        approverDropdownRefs.current[approverDropdownOpenIndex] &&
        !approverDropdownRefs.current[approverDropdownOpenIndex]?.contains(event.target as Node)
      ) {
        setApproverDropdownOpenIndex(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [approverDropdownOpenIndex]);

  const resetProposalForm = () => {
    setProposalForm({
      departmentId: '',
      vendorId: '',
      invoiceType: '',
      currency: '',
      amount: '',
      proposalDocument: null,
      additionalAttachments: [],
      approvers: ['']
    });
    setDepartmentDropdownOpen(false);
    setVendorDropdownOpen(false);
    setInvoiceTypeDropdownOpen(false);
    setCurrencyDropdownOpen(false);
    setApproverDropdownOpenIndex(null);
    setDepartmentSearchTerm('');
    setVendorSearchTerm('');
    setInvoiceTypeSearchTerm('');
    setCurrencySearchTerm('');
    setFormErrors({});
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (attachmentInputRef.current) {
      attachmentInputRef.current.value = '';
    }
  };
  const handleAdditionalAttachmentsChange = (files: FileList | null) => {
    if (!files || files.length === 0) {
      return;
    }

    setProposalForm((prev) => {
      const existing = [...prev.additionalAttachments];
      Array.from(files).forEach((file) => {
        if (existing.length < 5) {
          existing.push(file);
        }
      });
      return { ...prev, additionalAttachments: existing };
    });

    if (attachmentInputRef.current) {
      attachmentInputRef.current.value = '';
    }
  };

  const handleRemoveAdditionalAttachment = (index: number) => {
    setProposalForm((prev) => ({
      ...prev,
      additionalAttachments: prev.additionalAttachments.filter((_, i) => i !== index)
    }));
  };

  const validateProposalForm = (isEdit = false) => {
    const errors: typeof formErrors = {};

    // For new proposals, only validate department and proposal document
    if (!isEdit) {
      if (!proposalForm.proposalDocument) {
        errors.proposalDocument = 'Proposal document is required';
      }
    } else {
      // For edits, validate all fields
    if (!proposalForm.vendorId) {
      errors.vendorId = 'Vendor is required';
    }

    if (!proposalForm.invoiceType) {
      errors.invoiceType = 'Invoice type is required';
    }

    if (!proposalForm.currency) {
      errors.currency = 'Currency is required';
    }

    const amountValue = parseFloat(proposalForm.amount);
    if (!proposalForm.amount || isNaN(amountValue) || amountValue <= 0) {
      errors.amount = 'Enter a valid amount';
    }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };


  useEffect(() => {
    if (!proposalForm.currency && currencies.length > 0) {
      const defaultCurrency =
        currencies.find((currency) => currency.is_default)?.name || currencies[0]?.name || '';
      if (defaultCurrency) {
        setProposalForm((prev) => ({ ...prev, currency: defaultCurrency }));
      }
    }
  }, [currencies, proposalForm.currency]);

  const handleProposalSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validateProposalForm(false)) {
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      
      if (user?.department_id) {
        formData.append('department_id', user?.department_id.toString());
      }

      // Append proposal document
      if (proposalForm.proposalDocument) {
        formData.append('proposal_document', proposalForm.proposalDocument);
      }

      const response = await financialPowersAPI.create(formData);
      if (response?.success) {
        toast.success('Financial approval request sent successfully');
        setShowProposalModal(false);
        resetProposalForm();
        
        // Open the proposal details modal
        if (response.data?.id) {
          await handleViewProposal(response.data.id);
        } else {
          // If ID is not in response, fetch the latest proposal
          try {
            const listRes = await financialPowersAPI.getAll({ page: 1, limit: 1 });
            if (listRes?.success && listRes.data?.financial_powers && listRes.data.financial_powers.length > 0) {
              const latest = listRes.data.financial_powers[0];
              await handleViewProposal(latest.id);
            } else {
              // Fallback: refresh list
              fetchFinancialPowers();
            }
          } catch (fetchError) {
            console.error('Error fetching latest proposal:', fetchError);
            fetchFinancialPowers();
          }
        }
      } else {
        errorModal(response?.error || 'Failed to create proposal');
      }
    } catch (error: any) {
      console.error('Failed to create proposal', error);
      errorModal(error?.response?.data?.error || 'Failed to create proposal');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowProposalModal(false);
    resetProposalForm();
  };

  const handleViewProposal = async (id: number) => {
    setLoadingDetails(true);
    setShowDetailModal(true);
    try {
      const [proposalResponse, auditResponse] = await Promise.all([
        financialPowersAPI.getById(id),
        financialPowersAPI.getAuditTrail(id).catch(() => ({ success: false, data: null }))
      ]);
      
      if (proposalResponse?.success) {
        setSelectedProposal(proposalResponse.data);
        // Reset approver notes and reference name when opening modal
        setApproverNotes({});
        setReferenceName('');
      } else {
        errorModal('Failed to load proposal details');
        setShowDetailModal(false);
      }

      if (auditResponse?.success) {
        setAuditTrailData(auditResponse.data);
      }
    } catch (error) {
      console.error('Failed to fetch proposal details', error);
      errorModal('Failed to load proposal details');
      setShowDetailModal(false);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleApproverDecision = async (
    approverId: number,
    decision: 'approved' | 'rejected'
  ) => {
    if (!selectedProposal) return;
    const approvalKey = `${approverId}-${decision}`;
    
    try {
      setSubmittingApproval(approvalKey);
      const notes = approverNotes[approverId] || '';
      
      // Check if this is the last approver
      const isLastApprover = selectedProposal.approver_details && 
        selectedProposal.approver_details.length > 0 &&
        selectedProposal.approver_details[selectedProposal.approver_details.length - 1]?.approver_id === approverId;
      
      // if (isLastApprover && decision === 'approved' && !referenceName.trim()) {
      //   errorModal('Reference name is required for the last approver');
      //   setSubmittingApproval(null);
      //   return;
      // }

      // Call the approve API
      const response = await financialPowersAPI.approve(
        selectedProposal.id,
        decision,
        notes,
        isLastApprover && decision === 'approved' ? referenceName : undefined
      );
      
      if (response.success) {
        toast.success(`Proposal ${decision} successfully`);
        // Refresh proposal data
        const updatedResponse = await financialPowersAPI.getById(selectedProposal.id);
        if (updatedResponse.success) {
          setSelectedProposal(updatedResponse.data);
          // Clear notes and reference name
          setApproverNotes({});
          setReferenceName('');
        }
        // Refresh audit trail
        const auditResponse = await financialPowersAPI.getAuditTrail(selectedProposal.id).catch(() => ({ success: false, data: null }));
        if (auditResponse?.success) {
          setAuditTrailData(auditResponse.data);
        }
        // Close the modal on success
        setShowDetailModal(false);
      } else {
        errorModal(response.error || `Failed to ${decision} proposal`);
      }
    } catch (error: any) {
      console.error('Approver decision failed:', error);
      errorModal(error?.response?.data?.error || `Failed to ${decision} proposal`);
    } finally {
      setSubmittingApproval(null);
    }
  };

  const handleDeleteProposal = async (id: number) => {
    confirmToast('Are you sure you want to delete this financial approval? This action cannot be undone.', async () => {
      setDeleting(true);
      try {
        const response = await financialPowersAPI.delete(id);
        if (response?.success) {
          toast.success('Financial approval deleted successfully');
          fetchFinancialPowers();
        } else {
          errorModal(response?.error || 'Failed to delete financial approval');
        }
      } catch (error: any) {
        console.error('Failed to delete financial approval', error);
        errorModal(error?.response?.data?.error || 'Failed to delete financial approval');
      } finally {
        setDeleting(false);
      }
    });
  };

  const handleViewProposalDocument = async (url: string, filename: string) => {
    setLoadingAttachment(true);
    setCurrentAttachmentName(filename || 'Proposal Document');
    
    try {
      if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
        try {
          const response = await fetch(url, {
            method: 'GET',
            mode: 'cors',
            credentials: 'include',
            headers: {
              'Accept': 'application/pdf,image/*,*/*'
            }
          });
          
          if (response.ok) {
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            
            // Remove PDF toolbar by appending #toolbar=0
            const contentType = response.headers.get('content-type') || '';
            const urlWithToolbarRemoved = contentType.includes('pdf') || filename.toLowerCase().endsWith('.pdf')
              ? `${blobUrl}#toolbar=0` 
              : blobUrl;
            
            setCurrentAttachmentUrl(urlWithToolbarRemoved);
            setOriginalAttachmentUrl(url);
            setShowAttachmentViewer(true);
          } else {
            throw new Error(`Failed to fetch: ${response.status} ${response.statusText}`);
          }
        } catch (fetchError: any) {
          console.error('Fetch failed, opening in new tab:', fetchError);
          // If fetch fails, open in new tab as fallback
          window.open(url, '_blank');
          toast.success('Opening proposal document in new tab');
          setLoadingAttachment(false);
          return;
        }
      } else {
        throw new Error('No valid URL provided');
      }
    } catch (error: any) {
      console.error('Error viewing proposal document:', error);
      errorModal('Failed to load proposal document. Please try again.');
    } finally {
      setLoadingAttachment(false);
    }
  };

  const closeAttachmentViewer = () => {
    // Revoke blob URL if it was created
    if (currentAttachmentUrl && currentAttachmentUrl.startsWith('blob:')) {
      window.URL.revokeObjectURL(currentAttachmentUrl);
    }
    setShowAttachmentViewer(false);
    setCurrentAttachmentUrl('');
    setCurrentAttachmentName('');
    setOriginalAttachmentUrl('');
  };

  const handleEditProposal = (proposal: FinancialProposal) => {
    navigate(`/financial-power-proposal/${proposal.id}`);
  };

  const handleUpdateProposal = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editingProposal) return;
    
    if (!validateProposalForm(true)) {
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      
      if (proposalForm.departmentId) {
        formData.append('department_id', proposalForm.departmentId);
      }
      if (proposalForm.vendorId) {
        formData.append('vendor_id', proposalForm.vendorId);
      }
      formData.append('invoice_type', proposalForm.invoiceType);
      formData.append('currency', proposalForm.currency);
      formData.append('amount', proposalForm.amount);

      // Append proposal document if new one is uploaded
      if (proposalForm.proposalDocument) {
        formData.append('proposal_document', proposalForm.proposalDocument);
      }

      // Append additional attachments
      proposalForm.additionalAttachments.forEach((file) => {
        formData.append('additional_attachments', file);
      });

      const response = await financialPowersAPI.update(editingProposal.id, formData);
      if (response?.success) {
        toast.success('Financial approval updated successfully');
        setShowEditModal(false);
        setEditingProposal(null);
        resetProposalForm();
        fetchFinancialPowers();
        // Refresh the detail view if it's open
        if (selectedProposal?.id === editingProposal.id) {
          handleViewProposal(editingProposal.id);
        }
      } else {
        errorModal(response?.error || 'Failed to update financial approval');
      }
    } catch (error: any) {
      console.error('Failed to update financial approval', error);
      errorModal(error?.response?.data?.error || 'Failed to update financial approval');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingProposal(null);
    resetProposalForm();
  };

  const getStatusBadge = (status: string) => {
    let config: { bg: string; text: string; label: string };
    
    if (status === 'approved') {
      config = { bg: 'bg-green-100 text-green-800', text: 'Approved', label: 'Approved' };
    } else if (status === 'rejected') {
      config = { bg: 'bg-red-100 text-red-800', text: 'Rejected', label: 'Rejected' };
    } else if (status?.startsWith('pending_approver_')) {
      const approverNum = status.replace('pending_approver_', '');
      config = { bg: 'bg-blue-100 text-blue-800', text: 'Under Review', label: `Pending Approver ${approverNum}` };
    } else {
      config = { bg: 'bg-yellow-100 text-yellow-800', text: 'Pending', label: 'Pending' };
    }

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Financial Approval</h1>
          <p className="mt-1 text-sm text-gray-600">
            Track approvals that require financial approval across departments.
          </p>
        </div>
        {userPermissions === "invoice_initiator" && (
        <button
          className="btn-primary self-start md:self-auto flex items-center space-x-2"
          onClick={() => setShowProposalModal(true)}
        >
          <Plus className="h-4 w-4" />
          <span>New Financial Approval</span>
        </button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center min-w-0">
              <div className="p-2 bg-primary-100 rounded-lg flex-shrink-0">
                <FileText className="h-6 w-6 text-primary-600" />
              </div>
              <div className="ml-4 min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-600 truncate">Total Financial Approvals</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center min-w-0">
              <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                <Check className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4 min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-600 truncate">Approved</p>
                <p className="text-2xl font-bold text-gray-900">{stats.approved}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center min-w-0">
              <div className="p-2 bg-yellow-100 rounded-lg flex-shrink-0">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4 min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-600 truncate">Pending</p>
                <p className="text-2xl font-bold text-gray-900">{stats.pending}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center min-w-0">
              <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                <AlertCircle className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4 min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-600 truncate">Under Review</p>
                <p className="text-2xl font-bold text-gray-900">{stats.underReview}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-3 sm:space-y-0">
            <div className="flex-1">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="input-field pl-10"
                  placeholder="Search financial approvals, vendors, or references..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="w-full sm:w-64">
              <select
                className="input-field w-full"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Proposals Table */}
      <div className="card">
        <div className="card-header flex flex-col gap-1">
          <h3 className="text-lg font-semibold text-gray-900">Financial Approvals</h3>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <p className="mt-2 text-sm text-gray-500">Loading financial approvals...</p>
            </div>
          ) : financialPowers.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No financial approvals found</h3>
              <p className="mt-1 text-sm text-gray-500">Try adjusting your search or filters.</p>
            </div>
          ) : (
            <>
            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr>
                      <th className="table-header-cell">ID</th>
                    <th className="table-header-cell">Vendor</th>
                    <th className="table-header-cell">Department</th>
                    {/* <th className="table-header-cell">Invoice Type</th> */}
                    <th className="table-header-cell">Amount</th>
                      <th className="table-header-cell">Created Date</th>
                    <th className="table-header-cell">Status</th>
                    <th className="table-header-cell">Reference Number</th>
                    <th className="table-header-cell">Reference Name</th>
                    <th className="table-header-cell text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                    {financialPowers.map((proposal) => (
                    <tr key={proposal.id} className="table-row">
                      <td className="table-cell">
                        <div>
                            <p className="text-sm font-medium text-gray-900">FP-{proposal.id}</p>
                        </div>
                      </td>
                      <td className="table-cell">
                          <p className="text-sm text-gray-900">{proposal.vendor_name || 'N/A'}</p>
                      </td>
                      <td className="table-cell">
                          <p className="text-sm text-gray-900">{proposal.department_name || 'N/A'}</p>
                      </td>
                      {/* <td className="table-cell">
                          <p className="text-sm text-gray-900">{proposal.invoice_type === 'fixed' ? 'Fixed Payment' : 'Variable Payment'}</p>
                      </td> */}
                      <td className="table-cell">
                        {proposal?.amount && proposal?.currency && (
                          <p className={`text-sm font-medium text-gray-900 ${getCurrencyFontClass(proposal?.currency)}`}>
                            {formatCurrency(proposal?.amount, proposal?.currency)}
                          </p>
                        )}
                      </td>
                      <td className="table-cell">
                        <p className="text-sm text-gray-900">
                            {new Date(proposal.created_at).toLocaleDateString()}
                        </p>
                      </td>
                      <td className="table-cell">
                        {getStatusBadge(proposal.status)}
                      </td>
                      <td className="table-cell">
                        <p className="text-sm text-gray-900">{proposal.reference_number || 'N/A'}</p>
                      </td>
                      <td className="table-cell">
                        <p className="text-sm text-gray-900">{proposal.reference_name || 'N/A'}</p>
                      </td>
                      <td className="table-cell text-right space-x-2">
                          <div className="flex items-center justify-end space-x-2">
                            <button 
                              onClick={() => handleViewProposal(proposal.id)}
                              className="text-primary-600 hover:text-primary-900" 
                              title="View Proposal"
                            >
                          <Eye className="h-4 w-4" />
                        </button>
                        {proposal.status === 'pending' && (
                            <button 
                              onClick={() => handleDeleteProposal(proposal.id)}
                              disabled={deleting}
                              className="text-red-600 hover:text-red-900 disabled:opacity-50" 
                              title="Delete Proposal"
                            >
                          <Trash2 className="h-4 w-4" />
                        </button>
                        )}
                          </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              {pagination && (
                <Pagination
                  pagination={{
                    page: pagination.page,
                    limit: pagination.limit,
                    total: pagination.total,
                    total_pages: pagination.total_pages || pagination.totalPages,
                  }}
                  onPageChange={(p) => setPagination(prev => ({ 
                    ...prev, 
                    page: p,
                    totalPages: prev.total_pages || prev.totalPages,
                  }))}
                  onLimitChange={(l) => {
                    const newLimit = Math.min(100, Math.max(1, l));
                    setPagination(prev => ({ 
                      ...prev, 
                      limit: newLimit, 
                      page: 1,
                      totalPages: prev.total_pages || prev.totalPages,
                    }));
                  }}
                  loading={loading}
                  className="px-4 pb-4"
                />
              )}
            </>
          )}
        </div>
      </div>

      {showProposalModal && (
        <div style={{ margin: '0px !important' }}>
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-2xl bg-white rounded-xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-white">New Financial Approval</h3>
                <button
                  onClick={handleCloseModal}
                  className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <form className="space-y-4" onSubmit={handleProposalSubmit}>
                {/* <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Department <span className="text-red-500">*</span>
                  </label>
                  <div ref={departmentDropdownRef} className="relative">
                    <button
                      type="button"
                      className={`input-field w-full flex items-center justify-between text-left ${formErrors.departmentId ? 'border-red-500' : ''}`}
                      onClick={() => {
                        setDepartmentDropdownOpen((prev) => !prev);
                        setVendorDropdownOpen(false);
                        setInvoiceTypeDropdownOpen(false);
                        setCurrencyDropdownOpen(false);
                        setApproverDropdownOpenIndex(null);
                      }}
                    >
                      <span className={proposalForm.departmentId ? 'text-gray-900' : 'text-gray-500'}>
                        {proposalForm.departmentId
                          ? departments.find((dept) => dept.id.toString() === proposalForm.departmentId)?.department
                          : 'Select Department'}
                      </span>
                      <ChevronDown
                        className={`h-4 w-4 text-gray-400 transition-transform ${
                          departmentDropdownOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {departmentDropdownOpen && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-hidden">
                        <div className="p-2 border-b border-gray-100">
                          <div className="relative">
                            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                            <input
                              type="text"
                              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="Search departments..."
                              value={departmentSearchTerm}
                              onChange={(e) => setDepartmentSearchTerm(e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {departments
                            .filter((dept) =>
                              dept.department.toLowerCase().includes(departmentSearchTerm.toLowerCase())
                            )
                            .map((dept) => (
                              <button
                                key={dept.id}
                                type="button"
                                className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-primary-50 hover:text-primary-900"
                                onClick={() => {
                                  setProposalForm((prev) => ({
                                    ...prev,
                                    departmentId: dept.id.toString(),
                                    vendorId: ''
                                  }));
                                  setDepartmentDropdownOpen(false);
                                  setDepartmentSearchTerm('');
                                  setFormErrors((prev) => ({ ...prev, departmentId: undefined }));
                                }}
                              >
                                {dept.department}
                              </button>
                            ))}
                          {departments.filter((dept) =>
                            dept.department.toLowerCase().includes(departmentSearchTerm.toLowerCase())
                          ).length === 0 && (
                            <div className="px-4 py-2 text-sm text-gray-500 text-center">No departments found</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  {formErrors.departmentId && (
                    <p className="text-xs text-red-500 mt-1">{formErrors.departmentId}</p>
                  )}
                </div> */}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Approval Document <span className="text-red-500">*</span>
                  </label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf"
                    className={`input-field ${formErrors.proposalDocument ? 'border-red-500' : ''}`}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const maxSize = 10 * 1024 * 1024; // 10MB in bytes
                        if (file.size > maxSize) {
                          setFormErrors((prev) => ({ 
                            ...prev, 
                            proposalDocument: 'File size exceeds 10MB limit. Please select a smaller file.' 
                          }));
                          e.target.value = ''; // Clear the input
                          setProposalForm((prev) => ({
                            ...prev,
                            proposalDocument: null
                          }));
                        } else {
                          setProposalForm((prev) => ({
                            ...prev,
                            proposalDocument: file
                          }));
                          setFormErrors((prev) => ({ ...prev, proposalDocument: undefined }));
                        }
                      } else {
                        setProposalForm((prev) => ({
                          ...prev,
                          proposalDocument: null
                        }));
                        setFormErrors((prev) => ({ ...prev, proposalDocument: undefined }));
                      }
                    }}
                  />
                  <p className="text-xs text-gray-500 mt-1">Supported format: PDF (max 10MB)</p>
                  {formErrors.proposalDocument && (
                    <p className="text-xs text-red-500 mt-1">{formErrors.proposalDocument}</p>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-4">
                  <button type="button" className="btn-secondary w-full sm:w-auto" onClick={handleCloseModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary w-full sm:w-auto" disabled={submitting}>
                    {submitting ? 'Uploading...' : 'Upload Proposal'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        </div>
      )}

      {/* Edit Proposal Modal */}
      {showEditModal && editingProposal && (
        <div style={{ margin: '0px !important' }}>
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-2xl bg-white rounded-xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-white">Edit Financial Approval</h3>
                <button
                  onClick={handleCloseEditModal}
                  className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <form className="space-y-4" onSubmit={handleUpdateProposal}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Department <span className="text-red-500">*</span>
                  </label>
                  <div ref={departmentDropdownRef} className="relative">
                    <button
                      type="button"
                      className={`input-field w-full flex items-center justify-between text-left ${formErrors.departmentId ? 'border-red-500' : ''}`}
                      onClick={() => {
                        setDepartmentDropdownOpen((prev) => !prev);
                        setVendorDropdownOpen(false);
                        setInvoiceTypeDropdownOpen(false);
                        setCurrencyDropdownOpen(false);
                        setApproverDropdownOpenIndex(null);
                      }}
                    >
                      <span className={proposalForm.departmentId ? 'text-gray-900' : 'text-gray-500'}>
                        {proposalForm.departmentId
                          ? departments.find((dept) => dept.id.toString() === proposalForm.departmentId)?.department
                          : 'Select Department'}
                      </span>
                      <ChevronDown
                        className={`h-4 w-4 text-gray-400 transition-transform ${
                          departmentDropdownOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {departmentDropdownOpen && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-hidden">
                        <div className="p-2 border-b border-gray-100">
                          <div className="relative">
                            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                            <input
                              type="text"
                              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="Search departments..."
                              value={departmentSearchTerm}
                              onChange={(e) => setDepartmentSearchTerm(e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {departments
                            .filter((dept) =>
                              dept.department.toLowerCase().includes(departmentSearchTerm.toLowerCase())
                            )
                            .map((dept) => (
                              <button
                                key={dept.id}
                                type="button"
                                className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-primary-50 hover:text-primary-900"
                                onClick={() => {
                                  setProposalForm((prev) => ({
                                    ...prev,
                                    departmentId: dept.id.toString(),
                                    vendorId: ''
                                  }));
                                  setDepartmentDropdownOpen(false);
                                  setDepartmentSearchTerm('');
                                  setFormErrors((prev) => ({ ...prev, departmentId: undefined }));
                                }}
                              >
                                {dept.department}
                              </button>
                            ))}
                          {departments.filter((dept) =>
                            dept.department.toLowerCase().includes(departmentSearchTerm.toLowerCase())
                          ).length === 0 && (
                            <div className="px-4 py-2 text-sm text-gray-500 text-center">No departments found</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  {formErrors.departmentId && (
                    <p className="text-xs text-red-500 mt-1">{formErrors.departmentId}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vendor <span className="text-red-500">*</span>
                  </label>
                  <div ref={vendorDropdownRef} className="relative">
                    <button
                      type="button"
                      className={`input-field w-full flex items-center justify-between text-left ${formErrors.vendorId ? 'border-red-500' : ''}`}
                      onClick={() => {
                        setVendorDropdownOpen((prev) => !prev);
                        setDepartmentDropdownOpen(false);
                        setInvoiceTypeDropdownOpen(false);
                        setCurrencyDropdownOpen(false);
                        setApproverDropdownOpenIndex(null);
                      }}
                    >
                      <span
                        className={
                          proposalForm.vendorId ? 'text-gray-900' : 'text-gray-500'
                        }
                      >
                        {proposalForm.vendorId
                          ? filteredVendors.find((vendor) => vendor.id.toString() === proposalForm.vendorId)?.name
                          : 'Select Vendor'}
                      </span>
                      <ChevronDown
                        className={`h-4 w-4 text-gray-400 transition-transform ${
                          vendorDropdownOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {vendorDropdownOpen && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-hidden">
                        <div className="p-2 border-b border-gray-100">
                          <div className="relative">
                            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                            <input
                              type="text"
                              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="Search vendors..."
                              value={vendorSearchTerm}
                              onChange={(e) => setVendorSearchTerm(e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {filteredVendors
                            .filter((vendor) =>
                              vendor.name.toLowerCase().includes(vendorSearchTerm.toLowerCase())
                            )
                            .map((vendor) => (
                              <button
                                key={vendor.id}
                                type="button"
                                className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-primary-50 hover:text-primary-900"
                                onClick={() => {
                                  setProposalForm((prev) => ({
                                    ...prev,
                                    vendorId: vendor.id.toString()
                                  }));
                                  setVendorDropdownOpen(false);
                                  setVendorSearchTerm('');
                                  setFormErrors((prev) => ({ ...prev, vendorId: undefined }));
                                }}
                              >
                                {vendor.name}
                              </button>
                            ))}
                          {filteredVendors.filter((vendor) =>
                            vendor.name.toLowerCase().includes(vendorSearchTerm.toLowerCase())
                          ).length === 0 && (
                            <div className="px-4 py-2 text-sm text-gray-500 text-center">
                              {filteredVendors.length ? 'No vendors found' : 'No vendors available'}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  {formErrors.vendorId && (
                    <p className="text-xs text-red-500 mt-1">{formErrors.vendorId}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Invoice Type <span className="text-red-500">*</span>
                  </label>
                  <div ref={invoiceTypeDropdownRef} className="relative">
                    <button
                      type="button"
                      className={`input-field w-full flex items-center justify-between text-left ${formErrors.invoiceType ? 'border-red-500' : ''}`}
                      onClick={() => {
                        setInvoiceTypeDropdownOpen((prev) => !prev);
                        setDepartmentDropdownOpen(false);
                        setVendorDropdownOpen(false);
                        setCurrencyDropdownOpen(false);
                        setApproverDropdownOpenIndex(null);
                      }}
                    >
                      <span className={proposalForm.invoiceType ? 'text-gray-900' : 'text-gray-500'}>
                        {proposalForm.invoiceType
                          ? invoiceTypeOptions.find((option) => option.id === proposalForm.invoiceType)?.label
                          : 'Select Invoice Type'}
                      </span>
                      <ChevronDown
                        className={`h-4 w-4 text-gray-400 transition-transform ${
                          invoiceTypeDropdownOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {invoiceTypeDropdownOpen && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-hidden">
                        <div className="p-2 border-b border-gray-100">
                          <div className="relative">
                            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                            <input
                              type="text"
                              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="Search invoice types..."
                              value={invoiceTypeSearchTerm}
                              onChange={(e) => setInvoiceTypeSearchTerm(e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {invoiceTypeOptions
                            .filter((option) =>
                              option.label.toLowerCase().includes(invoiceTypeSearchTerm.toLowerCase())
                            )
                            .map((option) => (
                              <button
                                key={option.id}
                                type="button"
                                className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-primary-50 hover:text-primary-900"
                                onClick={() => {
                                  setProposalForm((prev) => ({
                                    ...prev,
                                    invoiceType: option.id
                                  }));
                                  setInvoiceTypeDropdownOpen(false);
                                  setInvoiceTypeSearchTerm('');
                                  setFormErrors((prev) => ({ ...prev, invoiceType: undefined }));
                                }}
                              >
                                {option.label}
                              </button>
                            ))}
                          {invoiceTypeOptions.filter((option) =>
                            option.label.toLowerCase().includes(invoiceTypeSearchTerm.toLowerCase())
                          ).length === 0 && (
                            <div className="px-4 py-2 text-sm text-gray-500 text-center">No invoice types found</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  {formErrors.invoiceType && (
                    <p className="text-xs text-red-500 mt-1">{formErrors.invoiceType}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Currency <span className="text-red-500">*</span>
                  </label>
                  <div ref={currencyDropdownRef} className="relative">
                    <button
                      type="button"
                      className={`input-field w-full flex items-center justify-between text-left ${formErrors.currency ? 'border-red-500' : ''}`}
                      onClick={() => {
                        setCurrencyDropdownOpen((prev) => !prev);
                        setDepartmentDropdownOpen(false);
                        setVendorDropdownOpen(false);
                        setInvoiceTypeDropdownOpen(false);
                        setApproverDropdownOpenIndex(null);
                      }}
                    >
                      <span className={proposalForm.currency ? 'text-gray-900' : 'text-gray-500'}>
                        {proposalForm.currency
                          ? filteredCurrencies.find((currency) => currency.name === proposalForm.currency)?.name
                          : 'Select Currency'}
                      </span>
                      <ChevronDown
                        className={`h-4 w-4 text-gray-400 transition-transform ${
                          currencyDropdownOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {currencyDropdownOpen && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-hidden">
                        <div className="p-2 border-b border-gray-100">
                          <div className="relative">
                            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                            <input
                              type="text"
                              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="Search currencies..."
                              value={currencySearchTerm}
                              onChange={(e) => setCurrencySearchTerm(e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {filteredCurrencies.map((currency) => (
                            <button
                              key={currency.id}
                              type="button"
                              className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-primary-50 hover:text-primary-900"
                              onClick={() => {
                                setProposalForm((prev) => ({
                                  ...prev,
                                  currency: currency.name
                                }));
                                setCurrencyDropdownOpen(false);
                                setCurrencySearchTerm('');
                                setFormErrors((prev) => ({ ...prev, currency: undefined }));
                              }}
                            >
                              {currency.name}
                            </button>
                          ))}
                          {filteredCurrencies.length === 0 && (
                            <div className="px-4 py-2 text-sm text-gray-500 text-center">
                              {currencies.length ? 'No currencies found' : 'No currencies available'}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  {formErrors.currency && (
                    <p className="text-xs text-red-500 mt-1">{formErrors.currency}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className={`input-field ${formErrors.amount ? 'border-red-500' : ''}`}
                    placeholder="Enter proposal amount"
                    value={proposalForm.amount}
                    onChange={(e) => {
                      setProposalForm((prev) => ({
                        ...prev,
                        amount: e.target.value
                      }));
                      setFormErrors((prev) => ({ ...prev, amount: undefined }));
                    }}
                  />
                  {formErrors.amount && (
                    <p className="text-xs text-red-500 mt-1">{formErrors.amount}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Proposal Document
                  </label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf"
                    className={`input-field ${formErrors.proposalDocument ? 'border-red-500' : ''}`}
                    onChange={(e) => {
                      setProposalForm((prev) => ({
                        ...prev,
                        proposalDocument: e.target.files?.[0] || null
                      }));
                      setFormErrors((prev) => ({ ...prev, proposalDocument: undefined }));
                    }}
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave empty to keep existing document. Supported format: PDF (max 50MB)</p>
                  {formErrors.proposalDocument && (
                    <p className="text-xs text-red-500 mt-1">{formErrors.proposalDocument}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Additional Attachment</label>
                  <input
                    ref={attachmentInputRef}
                    type="file"
                    multiple
                    className="input-field"
                    onChange={(e) => handleAdditionalAttachmentsChange(e.target.files)}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Optional supporting documents (PDF, DOCX, XLSX, CSV, images) — up to 20 files
                  </p>
                  {proposalForm.additionalAttachments.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {proposalForm.additionalAttachments.map((file, idx) => (
                        <div
                          key={`${file.name}-${idx}`}
                          className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-sm text-gray-700"
                        >
                          <span className="truncate pr-3">{file.name}</span>
                          <button
                            type="button"
                            className="text-gray-400 hover:text-red-500"
                            onClick={() => handleRemoveAdditionalAttachment(idx)}
                            aria-label={`Remove ${file.name}`}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-4">
                  <button type="button" className="btn-secondary w-full sm:w-auto" onClick={handleCloseEditModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary w-full sm:w-auto" disabled={submitting}>
                    {submitting ? 'Updating...' : 'Update Proposal'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        </div>
      )}

      {/* Proposal Detail Modal */}
      {showDetailModal && selectedProposal && (
        <div style={{ margin: '0px !important' }}>
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-5xl bg-white rounded-xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                    <FileText className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white">Financial Approval Details</h3>
                    <p className="text-primary-100 text-sm">FP-{selectedProposal.id}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setSelectedProposal(null);
                    setAuditTrailData(null);
                  }}
                  className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            {loadingDetails ? (
              <div className="p-12 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                <p className="mt-2 text-sm text-gray-500">Loading financial approval details...</p>
              </div>
            ) : (
              <div className="p-4 sm:p-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                  {/* Left Column - Basic Info */}
                  <div className="lg:col-span-2 space-y-4 sm:space-y-6">
                    {/* Proposal Overview Card */}
                    <div className="bg-gray-50 rounded-lg p-4 sm:p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-lg font-semibold text-gray-900">Financial Approval Overview</h4>
                        <div className="flex items-center space-x-2">
                          {getStatusBadge(selectedProposal.status)}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-3">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                              <FileText className="h-4 w-4 text-primary-600" />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide">Financial Approval ID</p>
                              <p className="text-sm font-medium text-gray-900">FP-{selectedProposal.id}</p>
                            </div>
                          </div>
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                              <Calendar className="h-4 w-4 text-purple-600" />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide">Created Date</p>
                              <p className="text-sm font-medium text-gray-900">
                                {new Date(selectedProposal.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                              <Building className="h-4 w-4 text-green-600" />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide">Vendor</p>
                              <p className="text-sm font-medium text-gray-900">{selectedProposal.vendor_name || 'N/A'}</p>
                            </div>
                          </div>
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                              <Building className="h-4 w-4 text-blue-600" />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide">Department</p>
                              <p className="text-sm font-medium text-gray-900">{selectedProposal.department_name || 'N/A'}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Financial Summary Card */}
                    <div className="bg-gradient-to-r from-green-50 to-primary-50 rounded-lg p-4 sm:p-6 border border-green-100">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Financial Summary</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {selectedProposal.invoice_type && (
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Invoice Type</p>
                            <p className="text-sm font-medium text-gray-900">{selectedProposal.invoice_type.toString().charAt(0).toUpperCase() + selectedProposal.invoice_type.toString().slice(1).toLowerCase()}</p>
                          </div>
                        )}
                        <div className="text-center sm:text-right">
                          <div className={`text-2xl font-semibold text-gray-700 ${getCurrencyFontClass(selectedProposal.currency || 'GBP')}`}>
                            {formatCurrency(selectedProposal.amount || 0, selectedProposal.currency || 'GBP')}
                          </div>
                          
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Total Amount</p>
                        </div>
                      </div>
                    </div>

                    {/* Approvers Section */}
                    {selectedProposal.approver_details && selectedProposal.approver_details.length > 0 && (
                      <div className="bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                        <div className="px-4 py-3 border-b border-gray-200">
                          <h4 className="text-lg font-semibold text-gray-900">Financial Approval Initiation</h4>
                        </div>
                        <div className="p-6">
                          <div className="space-y-4">
                            {selectedProposal.approver_details.map((approver: any, index: number) => {
                              // const isLastApprover = index === selectedProposal.approver_details!.length - 1;
                              const isCurrentUserApprover = String(user?.id || '') === String(approver.approver_id || '');
                              const canApprove = isCurrentUserApprover && 
                                approver.status !== 'approved' && 
                                approver.status !== 'rejected';
                              const isApproved = approver.status === 'approved';
                              const isRejected = approver.status === 'rejected';

                              return (
                                <div
                                  key={approver.id || index}
                                  className={`p-4 rounded-lg border transition-all duration-200 ${
                                    isApproved
                                      ? 'bg-green-50 border-green-200 hover:border-green-300'
                                      : isRejected
                                      ? 'bg-red-50 border-red-200 hover:border-red-300'
                                      : 'bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200 hover:border-orange-300'
                                  }`}
                                >
                                  <div className="flex items-center justify-between mb-3">
                                    <div>
                                      <p className="text-xs font-semibold text-orange-700 uppercase tracking-wider">
                                        LEVEL {index + 1} APPROVER
                                      </p>
                                    </div>
                                    <span
                                      className={`text-xs font-semibold capitalize ${
                                        isApproved
                                          ? 'text-green-700'
                                          : isRejected
                                          ? 'text-red-700'
                                          : 'text-gray-600'
                                      }`}
                                    >
                                      {approver.status || 'Pending'}
                                    </span>
                                  </div>
                                  <div
                                    className={`bg-white rounded-lg p-3 border ${
                                      isApproved
                                        ? 'border-green-100'
                                        : isRejected
                                        ? 'border-red-100'
                                        : 'border-orange-100'
                                    }`}
                                  >
                                    <div className="flex items-center">
                                      <div
                                        className={`${
                                          isApproved
                                            ? 'bg-green-100'
                                            : isRejected
                                            ? 'bg-red-100'
                                            : 'bg-gradient-to-br from-orange-100 to-orange-200'
                                        } w-8 h-8 rounded-full flex items-center justify-center mr-3`}
                                      >
                                        <span
                                          className={`text-xs font-semibold ${
                                            isApproved
                                              ? 'text-green-700'
                                              : isRejected
                                              ? 'text-red-700'
                                              : 'text-orange-700'
                                          }`}
                                        >
                                          {approver.first_name?.charAt(0) || ''}
                                          {approver.last_name?.charAt(0) || ''}
                                        </span>
                                      </div>
                                      <div>
                                        <p className="text-sm font-semibold text-gray-900">
                                          {approver.first_name} {approver.last_name}
                                        </p>
                                        <p className="text-xs text-gray-600">{approver.email}</p>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Note and action area */}
                                  {canApprove ? (
                                    <div className="mt-3 space-y-2">
                                      <label className="block text-xs font-medium text-gray-700">Note</label>
                                      <textarea
                                        rows={3}
                                        className="input-field w-full"
                                        value={approverNotes[approver.approver_id] || ''}
                                        onChange={(e) => {
                                          setApproverNotes(prev => ({
                                            ...prev,
                                            [approver.approver_id]: e.target.value
                                          }));
                                        }}
                                        placeholder="Enter your note..."
                                      />
                                      <div className="flex items-center space-x-2">
                                        <button
                                          type="button"
                                          disabled={submittingApproval === `${approver.approver_id}-rejected` || submittingApproval === `${approver.approver_id}-approved`}
                                          onClick={() => handleApproverDecision(approver.approver_id, 'rejected')}
                                          className="px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                        >
                                          {submittingApproval === `${approver.approver_id}-rejected` && (
                                            <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                          )}
                                          {submittingApproval === `${approver.approver_id}-rejected` ? 'Rejecting...' : 'Reject'}
                                        </button>
                                        <button
                                          type="button"
                                          disabled={submittingApproval === `${approver.approver_id}-approved` || submittingApproval === `${approver.approver_id}-rejected`}
                                          onClick={() => handleApproverDecision(approver.approver_id, 'approved')}
                                          className="px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                        >
                                          {submittingApproval === `${approver.approver_id}-approved` && (
                                            <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                          )}
                                          {submittingApproval === `${approver.approver_id}-approved` ? 'Approving...' : 'Approve'}
                                        </button>
                                      </div>
                                    </div>
                                  ) : approver.notes ? (
                                    <div className="mt-3 p-3 bg-white border rounded">
                                      <p className="text-xs font-semibold text-gray-700 mb-1">Note</p>
                                      <p className="text-sm text-gray-800">{approver.notes}</p>
                                      {/* {isLastApprover && approver.reference_name && (
                                        <div className="mt-2">
                                          <p className="text-xs font-semibold text-gray-700 mb-1">Reference Name</p>
                                          <p className="text-sm text-gray-800">{approver.reference_name}</p>
                                        </div>
                                      )} */}
                                    </div>
                                  ) : null}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Additional Attachments Section */}
                    {( (selectedProposal.additional_attachments && selectedProposal.additional_attachments.length > 0)) && (
                      <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
                        <div className="flex items-center mb-4">
                          <div className="w-8 h-8 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center mr-3">
                            <Upload className="h-4 w-4 text-gray-600" />
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900">Additional Attachments</h4>
                            <p className="text-xs text-gray-500">
                              {(() => {
                                let count = 0;
                                if (selectedProposal.additional_attachments) count += selectedProposal.additional_attachments.length;
                                return `${count} file(s)`;
                              })()}
                            </p>
                          </div>
                        </div>
                        <div className="space-y-3">

                          {/* Additional Attachments */}
                          {selectedProposal.additional_attachments &&
                            selectedProposal.additional_attachments.length > 0 &&
                            selectedProposal.additional_attachments.map((attachment: any, index: number) => (
                              <div
                                key={index}
                                className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all duration-200"
                              >
                                <div className="flex items-center flex-1 min-w-0">
                                  <div className="w-8 h-8 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center mr-3">
                                    <FileText className="h-4 w-4 text-blue-600" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">{attachment.filename || 'Attachment'}</p>
                                    <p className="text-xs text-gray-500">
                                      {attachment.size ? `${(attachment.size / 1024).toFixed(1)} KB` : attachment.type || 'Attachment'}
                                    </p>
                                  </div>
                                </div>
                                {attachment.url && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      handleViewProposalDocument(attachment.url, attachment.filename || 'Attachment');
                                    }}
                                    className="ml-3 px-3 py-1.5 text-xs font-medium bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 hover:text-purple-800 transition-colors duration-200 flex items-center"
                                  >
                                    <Eye className="h-4 w-4 mr-1" />
                                    <span>View</span>
                                  </button>
                                )}
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right Column - Vendor Info */}
                  <div className="space-y-4 sm:space-y-6">
                    {/* Quick Actions Card */}
                    <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h4>
                      <div className="space-y-3">
                        {((userPermissions === "invoice_initiator" && 
                           selectedProposal.status === 'pending') ||
                          (userPermissions === "admin" && selectedProposal.status !== 'approved' && selectedProposal.status !== 'rejected')) && (
                          <button
                            onClick={() => handleEditProposal(selectedProposal)}
                            className="flex items-center justify-center space-x-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors w-full"
                          >
                            <Edit className="h-4 w-4" />
                            <span>Edit Financial Approval</span>
                          </button>
                        )}
                        <button
                          onClick={() => {
                            if (selectedProposal.proposal_document_url) {
                              handleViewProposalDocument(
                                selectedProposal.proposal_document_url,
                                selectedProposal.proposal_document_filename || 'Proposal Document'
                              );
                            } else {
                              errorModal('Financial approval document not available');
                            }
                          }}
                          className="flex items-center justify-center space-x-2 bg-gray-700 hover:bg-gray-800 text-white px-4 py-2 rounded-lg transition-colors w-full"
                        >
                          <Eye className="h-4 w-4" />
                          <span>View Financial Approval</span>
                        </button>
                      </div>
                    </div>

                    {/* Proposal Status Trail */}
                    {auditTrailData && auditTrailData.audit_trail && auditTrailData.audit_trail.length > 0 && (
                      <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">Financial Approval Status</h4>
                        <div className="relative">
                          <div className="space-y-3">
                            {auditTrailData.audit_trail
                              .slice()
                              .reverse()
                              .map((trail: any, index: number, reversedArray: any[]) => {
                                // Check if this trail item contains "Rejected" keyword
                                const statusText = trail.action === 'UPDATE' && trail.description?.includes('Status changed')
                                  ? trail.details?.new_status || trail.description
                                  : trail.description || trail.action;
                                const isRejected = statusText && statusText.toLowerCase().includes('rejected');
                                
                                return (
                                  <div key={trail.id || index} className="relative flex items-start">
                                    {/* Timeline line segment - connects to next item (below current item) */}
                                    {index < reversedArray.length - 1 && (
                                      <div className={`absolute left-3 top-6 w-0.5 z-0 ${isRejected ? 'bg-red-600' : 'bg-green-300'}`} style={{ height: 'calc(100% + 0.75rem)' }}></div>
                                    )}
                                    {/* Timeline dot */}
                                    <div className={`relative z-10 flex items-center justify-center w-6 h-6 rounded-full border-2 border-white shadow-sm ${isRejected ? 'bg-red-600' : 'bg-green-600'}`}>
                                    </div>

                                    {/* Content */}
                                    <div className={`ml-3 flex-1 rounded-md p-3 border ${isRejected ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
                                      <div className="flex items-center justify-between mb-1">
                                        <span className={`text-sm font-semibold capitalize ${isRejected ? 'text-red-900' : 'text-gray-900'}`}>
                                          {statusText}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                          {new Date(trail.performed_at).toLocaleString()}
                                        </span>
                                      </div>

                                      <div className="text-xs text-gray-600 mb-1">
                                        By: {trail.performed_by?.name || 'Unknown'} {trail.performed_by?.email && `(${trail.performed_by.email})`}
                                      </div>

                                      {/* Show approver details when approvers are assigned */}
                                      {trail.action === 'CREATE' && trail.details?.approvers && Array.isArray(trail.details.approvers) && trail.details.approvers.length > 0 && (
                                        <div className="space-y-0.5 mt-2">
                                          {trail.details.approvers.map((approver: any, idx: number) => (
                                            <div key={idx} className="text-xs text-gray-600">
                                              <span className="font-medium">Approver {idx + 1}:</span> {approver.name || approver.email || 'Unknown'}
                                            </div>
                                          ))}
                                        </div>
                                      )}

                                      {/* Show detailed information with colored bars */}
                                      {(trail.details?.notes || trail.details?.old_status || trail.details?.new_status || trail.details?.reference_number || trail.details?.reference_name || trail.details?.amount) && (
                                        <div className="mt-2 grid grid-cols-1 md:grid-cols-1 gap-2">
                                          {trail.details.notes && (
                                            <div className="flex flex-col bg-gray-50 rounded border-l-4 border-gray-400 px-3 py-2">
                                              <span className="text-xs text-gray-700 font-semibold">Notes</span>
                                              <span className="text-sm text-gray-900">{trail.details.notes}</span>
                                            </div>
                                          )}
                                          {/* {trail.details.old_status && trail.details.new_status && (
                                            <div className="flex flex-col bg-primary-50 rounded border-l-4 border-primary-400 px-3 py-2">
                                              <span className="text-xs text-primary-700 font-semibold">Status Change</span>
                                              <span className="text-sm text-primary-900">
                                                {trail.details.old_status.charAt(0).toUpperCase() + trail.details.old_status.slice(1).toLowerCase()} → {trail.details.new_status.charAt(0).toUpperCase() + trail.details.new_status.slice(1).toLowerCase()}
                                              </span>
                                            </div>
                                          )} */}
                                          {trail.details.reference_number && (
                                            <div className="flex flex-col bg-yellow-50 rounded border-l-4 border-yellow-400 px-3 py-2">
                                              <span className="text-xs text-yellow-700 font-semibold">Reference Number</span>
                                              <span className="text-sm text-yellow-900">{trail.details.reference_number}</span>
                                            </div>
                                          )}
                                          {trail.details.reference_name && (
                                            <div className="flex flex-col bg-blue-50 rounded border-l-4 border-blue-400 px-3 py-2">
                                              <span className="text-xs text-blue-700 font-semibold">Reference Name</span>
                                              <span className="text-sm text-blue-900">{trail?.details?.reference_name}</span>
                                            </div>
                                          )}
                                          {trail.details.amount && (
                                            <div className="flex flex-col bg-green-50 rounded border-l-4 border-green-400 px-3 py-2">
                                              <span className="text-xs text-green-700 font-semibold">Amount</span>
                                              {trail?.details?.amount && trail?.details?.currency && (
                                                <span className={`text-sm text-green-900 ${getCurrencyFontClass(trail.details.currency || 'USD')}`}>
                                                  {formatCurrency(trail?.details?.amount, trail?.details?.currency || 'USD')}
                                                </span>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        </div>
      )}

      {/* Attachment Viewer Modal */}
      {showAttachmentViewer && currentAttachmentUrl && (
        <div style={{ margin: '0px !important' }}>
        <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-6xl bg-white rounded-xl shadow-2xl overflow-hidden max-h-[95vh] flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <FileText className="h-5 w-5 text-white" />
                <h3 className="text-xl font-semibold text-white truncate">
                  {currentAttachmentName}
                </h3>
              </div>
              <div className="flex items-center space-x-2">
                {originalAttachmentUrl && (
                  <button
                    onClick={() => {
                      window.open(originalAttachmentUrl, '_blank');
                    }}
                    className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                    title="Open in new tab"
                  >
                    <Eye className="h-5 w-5" />
                  </button>
                )}
                <a
                  href={currentAttachmentUrl}
                  download={currentAttachmentName}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                  title="Download"
                >
                  <Download className="h-5 w-5" />
                </a>
                <button
                  onClick={closeAttachmentViewer}
                  className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                  title="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content - iframe */}
            <div className="flex-1 overflow-hidden bg-gray-100 p-4">
              {loadingAttachment ? (
                <div className="flex items-center justify-center h-full">
                  <Spinner size="md" color="blue" />
                  <span className="ml-3 text-gray-600">Loading financial approval document...</span>
                </div>
              ) : (
                <iframe
                  src={currentAttachmentUrl}
                  className="w-full h-full min-h-[600px] rounded-lg shadow-lg border-0"
                  title={currentAttachmentName}
                  style={{ border: 'none' }}
                  onError={(e) => {
                    console.error('Iframe load error:', e);
                    errorModal('Failed to load in viewer. Opening in new tab...');
                    setTimeout(() => {
                      // If it's not a blob URL, open it
                      if (!currentAttachmentUrl.startsWith('blob:')) {
                        window.open(currentAttachmentUrl, '_blank');
                      } else if (originalAttachmentUrl) {
                        window.open(originalAttachmentUrl, '_blank');
                      }
                    }, 1000);
                  }}
                  onLoad={() => {
                    console.log('Iframe loaded successfully');
                  }}
                />
              )}
            </div>
          </div>
        </div>
        </div>
      )}

      {/* Attachment Viewer Modal */}
      {showAttachmentViewer && currentAttachmentUrl && (
        <div style={{ margin: '0px !important' }}>
        <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-6xl bg-white rounded-xl shadow-2xl overflow-hidden max-h-[95vh] flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <FileText className="h-5 w-5 text-white" />
                <h3 className="text-xl font-semibold text-white truncate">
                  {currentAttachmentName}
                </h3>
              </div>
              <div className="flex items-center space-x-2">
                {originalAttachmentUrl && (
                  <button
                    onClick={() => {
                      window.open(originalAttachmentUrl, '_blank');
                    }}
                    className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                    title="Open in new tab"
                  >
                    <Eye className="h-5 w-5" />
                  </button>
                )}
                <a
                  href={currentAttachmentUrl}
                  download={currentAttachmentName}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                  title="Download"
                >
                  <Download className="h-5 w-5" />
                </a>
                <button
                  onClick={closeAttachmentViewer}
                  className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                  title="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content - iframe */}
            <div className="flex-1 overflow-hidden bg-gray-100 p-4">
              {loadingAttachment ? (
                <div className="flex items-center justify-center h-full">
                  <Spinner size="md" color="blue" />
                  <span className="ml-3 text-gray-600">Loading financial approval document...</span>
                </div>
              ) : (
                <iframe
                  src={currentAttachmentUrl}
                  className="w-full h-full min-h-[600px] rounded-lg shadow-lg border-0"
                  title={currentAttachmentName}
                  style={{ border: 'none' }}
                  onError={(e) => {
                    console.error('Iframe load error:', e);
                    errorModal('Failed to load in viewer. Opening in new tab...');
                    setTimeout(() => {
                      // If it's not a blob URL, open it
                      if (!currentAttachmentUrl.startsWith('blob:')) {
                        window.open(currentAttachmentUrl, '_blank');
                      } else if (originalAttachmentUrl) {
                        window.open(originalAttachmentUrl, '_blank');
                      }
                    }, 1000);
                  }}
                  onLoad={() => {
                    console.log('Iframe loaded successfully');
                  }}
                />
              )}
            </div>
          </div>
        </div>
        </div>
      )}
    </div>
  );
};

export default FinancialPowerProposal;

